import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Pages
import LoginPage from "./pages/LoginPage";
import LoyaltyLoginPage from "./pages/LoyaltyLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import POSPage from "./pages/POSPage";
import AgendaPage from "./pages/AgendaPage";
import PublicBookingPage from "./pages/PublicBookingPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import AdminServicesPage from "./pages/AdminServicesPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminClientsPage from "./pages/AdminClientsPage";
import AdminRewardsPage from "./pages/AdminRewardsPage";
import AdminBranchesPage from "./pages/AdminBranchesPage";
import AdminFinancePage from "./pages/AdminFinancePage";
import AdminCashSessionsPage from "./pages/AdminCashSessionsPage";
import AdminDailySalesPage from "./pages/AdminDailySalesPage";
import AdminCashReportPage from "./pages/AdminCashReportPage";
import AdminDetailingPage from "./pages/AdminDetailingPage";
import AdminFinanceBalancePage from "./pages/AdminFinanceBalancePage";
import PublicQuotePage from "./pages/PublicQuotePage";

// Role constants to avoid inline array re-creation
const ROLES_ADMIN = ['admin'];
const ROLES_ADMIN_CAJERO = ['admin', 'cajero'];
const ROLES_ADMIN_CAJERO_OPERADOR = ['admin', 'cajero', 'operador', 'gerente'];
const ROLES_ADMIN_CAJERO_GERENTE = ['admin', 'cajero', 'gerente'];
const ROLES_ADMIN_GERENTE = ['admin', 'gerente'];
const ROLES_CLIENTE = ['cliente'];

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'cajero') return <Navigate to="/pos" replace />;
    if (user.role === 'operador') return <Navigate to="/agenda" replace />;
    if (user.role === 'gerente') return <Navigate to="/agenda" replace />;
    if (user.role === 'cliente') return <Navigate to="/cliente" replace />;
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Redirect if authenticated
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'cajero') return <Navigate to="/pos" replace />;
    if (user.role === 'operador') return <Navigate to="/agenda" replace />;
    if (user.role === 'gerente') return <Navigate to="/agenda" replace />;
    if (user.role === 'cliente') return <Navigate to="/cliente" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/lealtad" element={
        <PublicRoute>
          <LoyaltyLoginPage />
        </PublicRoute>
      } />
      <Route path="/agendar" element={<PublicBookingPage />} />
      <Route path="/cotizacion/:token" element={<PublicQuotePage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/servicios" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminServicesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/usuarios" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clientes" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_CAJERO_GERENTE}>
          <AdminClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/recompensas" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminRewardsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/sucursales" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminBranchesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/finanzas" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminFinancePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/finanzas/balance" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminFinanceBalancePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/caja" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_CAJERO_GERENTE}>
          <AdminCashSessionsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/caja/:sessionId/reporte" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_CAJERO_GERENTE}>
          <AdminCashReportPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ventas-diarias" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_GERENTE}>
          <AdminDailySalesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/detailing" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <AdminDetailingPage />
        </ProtectedRoute>
      } />
      
      {/* POS Route */}
      <Route path="/pos" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_CAJERO_GERENTE}>
          <POSPage />
        </ProtectedRoute>
      } />
      
      {/* Agenda Route */}
      <Route path="/agenda" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_CAJERO_OPERADOR}>
          <AgendaPage />
        </ProtectedRoute>
      } />
      
      {/* Client Portal */}
      <Route path="/cliente" element={
        <ProtectedRoute allowedRoles={ROLES_CLIENTE}>
          <ClientPortalPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
